import { IAlarmType, IGetGalleryAlarmTypeListResponse } from '@reolink-cloud/interface';

export interface IAlarmTypeTreeOption {
    id: number;
    parentId: number;
    key: string;
    label: string;
    iconUrl: string;
    sort: number | null;
    children: IAlarmTypeTreeOption[];
}

export interface IAlarmTypeSelectionState {
    checked: boolean;
    indeterminate: boolean;
}

export interface IDeviceAlarmTypeSupport {
    id: string;
    alarmTypes: string[];
}

export interface IAlarmTypeDisplay {
    text: string;
    iconUrls: string[];
}

interface IAlarmTypeGroup {
    alarmType: IAlarmType;
    items: IAlarmType[];
}

export const getGalleryAlarmTypeRequestUids = (
    selectedUids?: string[]
): [string] | undefined => selectedUids?.length === 1 ? [selectedUids[0]] : undefined;

export const getSupportedAlarmTypeKeys = (
    devices: IDeviceAlarmTypeSupport[],
    selectedUids?: string[]
): string[] => {

    const selectedUidSet = selectedUids?.length ? new Set( selectedUids ) : null;
    const supportedKeys = new Set<string>();

    for ( const device of devices ) {

        if ( selectedUidSet && !selectedUidSet.has( device.id )) continue;
        for ( const alarmType of device.alarmTypes ) supportedKeys.add( alarmType );

    }

    return Array.from( supportedKeys );

};

// 有 sort 的类型优先，按 sort 升序；相同或均缺失时通过稳定排序保留配置顺序。
export const sortAlarmTypes = ( a: IAlarmType, b: IAlarmType ): number => {

    const aSort = a.meta?.sort;
    const bSort = b.meta?.sort;

    if ( typeof aSort === 'number' && typeof bSort === 'number' ) {

        return aSort - bSort;

    }
    if ( typeof aSort === 'number' ) return -1;
    if ( typeof bSort === 'number' ) return 1;
    return 0;

};

const groupAlarmTypes = ( alarmTypes: IAlarmType[] ): Map<number, IAlarmTypeGroup> => {

    // 从接口配置顺序稳定排序，Map 的插入顺序也就代表各分组的展示顺序。
    const sortedAlarmTypes = [...alarmTypes].sort( sortAlarmTypes );
    const alarmTypeGroups = new Map<number, IAlarmTypeGroup>();

    for ( const alarmType of sortedAlarmTypes ) {

        alarmTypeGroups.set( alarmType.id, { alarmType, items: [] });

    }

    for ( const alarmType of sortedAlarmTypes ) {

        const parentGroup = alarmType.parentId === 0 ? undefined : alarmTypeGroups.get( alarmType.parentId );
        // 父类型缺失时独立成组，保留该类型的排序位置。
        const group = parentGroup ?? alarmTypeGroups.get( alarmType.id )!;

        // 父类型放组首，二级类型按已排好的顺序追加，避免跨一级分组交错。
        if ( alarmType.id === group.alarmType.id ) {

            group.items.unshift( alarmType );

        } else {

            group.items.push( alarmType );

        }

    }

    return alarmTypeGroups;

};

export const getAlarmTypeDisplay = (
    keys: string[],
    alarmTypes: IAlarmType[]
): IAlarmTypeDisplay => {

    if ( keys.length === 0 ) return { text: '', iconUrls: [] };

    const alarmTypeGroups = groupAlarmTypes( alarmTypes );
    const orderedAlarmTypes = Array.from( alarmTypeGroups.values()).flatMap( group => group.items );
    const remainingKeys = new Set( keys );
    const labels: string[] = [];
    const displayedPrimaryAlarmTypeIds = new Set<number>();
    const iconUrls: string[] = [];

    // 按同一排序结果生成展示：名称保留实际类型，图标只展示一级类型并按一级 id 去重。
    for ( const alarmType of orderedAlarmTypes ) {

        if ( !remainingKeys.delete( alarmType.key )) continue;

        const label = ( alarmType.i18n?.name || alarmType.name || alarmType.key ).trim();
        if ( label ) labels.push( label );

        const primaryAlarmType = alarmType.parentId === 0
            ? alarmType
            : alarmTypeGroups.get( alarmType.parentId )?.alarmType;
        const iconUrl = primaryAlarmType?.meta?.iconUrl;
        if ( !primaryAlarmType || !iconUrl || displayedPrimaryAlarmTypeIds.has( primaryAlarmType.id )) continue;

        displayedPrimaryAlarmTypeIds.add( primaryAlarmType.id );
        iconUrls.push( iconUrl );

    }

    // 未匹配到配置的 key 放到末尾，彼此保持事件中的原始顺序，不生成图标。
    for ( const key of remainingKeys ) {

        const label = key.trim();
        if ( label ) labels.push( label );

    }

    const MAX_VISIBLE_LABEL_COUNT = 2;
    const visibleText = labels.slice( 0, MAX_VISIBLE_LABEL_COUNT ).join( ', ' );

    return {
        text: labels.length > MAX_VISIBLE_LABEL_COUNT ? `${visibleText}...` : visibleText,
        iconUrls
    };

};

const toTreeOption = ( alarmType: IAlarmType ): IAlarmTypeTreeOption => ({
    id: alarmType.id,
    parentId: alarmType.parentId,
    key: alarmType.key,
    label: alarmType.i18n?.name ?? alarmType.key,
    iconUrl: alarmType.meta?.iconUrl ?? '',
    sort: typeof alarmType.meta?.sort === 'number' ? alarmType.meta.sort : null,
    children: []
});

export const buildAlarmTypeTree = (
    response: IGetGalleryAlarmTypeListResponse,
    supportedKeys: string[]
): IAlarmTypeTreeOption[] => {

    const supportedKeySet = new Set( supportedKeys );
    const alarmTypeGroups = groupAlarmTypes( response.alarmTypes );

    const result: IAlarmTypeTreeOption[] = [];
    for ( const { alarmType, items } of alarmTypeGroups.values()) {

        if ( alarmType.parentId !== 0 ) continue;

        const children = items
            .filter( child => child.id !== alarmType.id && supportedKeySet.has( child.key ))
            .map( toTreeOption );

        if ( !supportedKeySet.has( alarmType.key ) && children.length === 0 ) continue;

        result.push({
            ...toTreeOption( alarmType ),
            children
        });

    }

    return result;

};

export const getSelectableAlarmTypeKeys = (
    options: IAlarmTypeTreeOption[]
): string[] => options.flatMap( option =>
    option.children.length > 0 ? option.children.map( child => child.key ) : [option.key]
);

export const expandAlarmTypeSelection = (
    value: string[],
    options: IAlarmTypeTreeOption[]
): string[] => {

    if ( value.length === 0 ) return getSelectableAlarmTypeKeys( options );

    const selectedKeys = new Set<string>();
    const valueSet = new Set( value );

    for ( const option of options ) {

        if ( option.children.length === 0 ) {

            if ( valueSet.has( option.key )) selectedKeys.add( option.key );
            continue;

        }

        if ( valueSet.has( option.key )) {

            for ( const child of option.children ) selectedKeys.add( child.key );

        } else {

            for ( const child of option.children ) {

                if ( valueSet.has( child.key )) selectedKeys.add( child.key );

            }

        }

    }

    return Array.from( selectedKeys );

};

export const serializeAlarmTypeSelection = (
    selectedKeys: string[],
    options: IAlarmTypeTreeOption[]
): string[] => {

    const selectableKeys = getSelectableAlarmTypeKeys( options );
    const selectedSet = new Set( selectedKeys.filter( key => selectableKeys.includes( key )));

    if ( selectedSet.size === 0 || selectableKeys.every( key => selectedSet.has( key ))) return [];

    return selectableKeys.filter( key => selectedSet.has( key ));

};

export const getAlarmTypeQueryKeys = (
    selectedKeys: string[],
    options: IAlarmTypeTreeOption[]
): string[] => {

    const queryKeys = new Set( selectedKeys );
    const selectedKeySet = new Set( selectedKeys );

    for ( const option of options ) {

        if (
            option.children.length > 0
            && option.children.every( child => selectedKeySet.has( child.key ))
        ) queryKeys.add( option.key );

    }

    return Array.from( queryKeys );

};

export const getAlarmTypeSelectionState = (
    option: IAlarmTypeTreeOption,
    selectedKeys: string[]
): IAlarmTypeSelectionState => {

    const keys = option.children.length > 0
        ? option.children.map( child => child.key )
        : [option.key];
    const selectedSet = new Set( selectedKeys );
    const selectedCount = keys.filter( key => selectedSet.has( key )).length;

    return {
        checked: selectedCount === keys.length,
        indeterminate: selectedCount > 0 && selectedCount < keys.length
    };

};

export const getAllAlarmTypeSelectionState = (
    options: IAlarmTypeTreeOption[],
    selectedKeys: string[]
): IAlarmTypeSelectionState => {

    const selectableKeys = getSelectableAlarmTypeKeys( options );
    const selectedSet = new Set( selectedKeys );
    const selectedCount = selectableKeys.filter( key => selectedSet.has( key )).length;

    return {
        checked: selectableKeys.length > 0 && selectedCount === selectableKeys.length,
        indeterminate: selectedCount > 0 && selectedCount < selectableKeys.length
    };

};

export const reconcileAlarmTypeSelection = (
    value: string[],
    options: IAlarmTypeTreeOption[]
): string[] => {

    if ( value.length === 0 || options.length === 0 ) return [];

    const selectedKeys = expandAlarmTypeSelection( value, options );
    if ( selectedKeys.length === 0 ) return [];
    return serializeAlarmTypeSelection( selectedKeys, options );

};

export const getAlarmTypeSelectionLabels = (
    value: string[],
    options: IAlarmTypeTreeOption[]
): string[] => {

    if ( value.length === 0 ) return [];

    const valueSet = new Set( value );
    const result: string[] = [];

    for ( const option of options ) {

        if ( option.children.length === 0 ) {

            if ( valueSet.has( option.key )) result.push( option.label );
            continue;

        }

        if ( valueSet.has( option.key )) {

            result.push( ...option.children.map( child => child.label ));

        } else {

            result.push( ...option.children
                .filter( child => valueSet.has( child.key ))
                .map( child => child.label ));

        }

    }

    return result;

};
